/**
 * Запись основной видеозаписи персоны (§6.4, §71).
 *
 * Мобильные особенности, из-за которых это не просто MediaRecorder:
 *  - iOS Safari пишет в свои контейнеры, поэтому реальный mimeType выясняем
 *    у браузера, а не предполагаем webm (agents/web-persona-capture.md §5);
 *  - экран не должен гаснуть посреди записи — wake lock;
 *  - записанное нельзя потерять при случайном уходе со страницы.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { i18n } from '../../i18n';

/** Порядок предпочтения: то, что переживёт нормализацию на сервере. */
const PREFERRED_TYPES = [
  'video/mp4;codecs=avc1,mp4a',
  'video/mp4',
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
];

/**
 * Только звук — для дополнительной записи голоса (§10.1).
 *
 * Просить включить камеру ради голоса — лишний барьер: человек и так уже
 * записал себя один раз, а тут его просят снова причесаться. Плюс запись
 * без видео легче и грузится быстрее.
 */
const PREFERRED_AUDIO_TYPES = [
  'audio/mp4',
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
];

function pickMimeType(audioOnly: boolean): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  const list = audioOnly ? PREFERRED_AUDIO_TYPES : PREFERRED_TYPES;
  return list.find((type) => MediaRecorder.isTypeSupported(type));
}

export type RecorderState = 'idle' | 'ready' | 'recording' | 'recorded' | 'error';

export interface RecordedCapture {
  blob: Blob;
  url: string;
  durationSec: number;
  mimeType: string;
  hasAudio: boolean;
}

export function useRecorder(options: { audioOnly?: boolean } = {}) {
  const audioOnly = options.audioOnly ?? false;
  const [state, setState] = useState<RecorderState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [captured, setCaptured] = useState<RecordedCapture | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const wakeLockRef = useRef<{ release: () => Promise<void> } | null>(null);

  const stopTimer = () => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const requestPermission = useCallback(async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        ...(audioOnly
          ? {}
          : { video: { facingMode: 'user', width: { ideal: 1080 }, height: { ideal: 1920 } } }),
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;
      setState('ready');
      return stream;
    } catch (cause) {
      const name = (cause as { name?: string }).name;
      setError(
        name === 'NotAllowedError'
          ? i18n.t('capture.error.permission')
          : name === 'NotFoundError'
            ? i18n.t('capture.error.notFound')
            : i18n.t('capture.error.generic'),
      );
      setState('error');
      return null;
    }
  }, [audioOnly]);

  const start = useCallback(async () => {
    const stream = streamRef.current ?? (await requestPermission());
    if (!stream) return;

    if (stream.getAudioTracks().length === 0) {
      setError(i18n.t('capture.error.noMic'));
      setState('error');
      return;
    }

    chunksRef.current = [];
    const mimeType = pickMimeType(audioOnly);
    const recorder = new MediaRecorder(stream, {
      ...(mimeType ? { mimeType } : {}),
      // Битрейт задаём явно.
      //
      // По умолчанию браузер пишет около 2.5 Мбит/с — для кадра 1080×1920
      // этого мало: кодек сглаживает детали лица, и запись выходит мягкой.
      // Проверено на настоящей записи: резкость на лице 12.5 при пороге 40,
      // хотя человек в фокусе, свет ровный и камера стабильна.
      //
      // Из этого же кадра потом делается аватар, и потерянные тут детали
      // не вернёт никакой провайдер.
      ...(audioOnly ? {} : { videoBitsPerSecond: 8_000_000 }),
      audioBitsPerSecond: 128_000,
    });

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };

    recorder.onstop = () => {
      const type = recorder.mimeType || mimeType || 'video/webm';
      const blob = new Blob(chunksRef.current, { type });
      setCaptured({
        blob,
        url: URL.createObjectURL(blob),
        durationSec: (Date.now() - startedAtRef.current) / 1000,
        mimeType: type,
        hasAudio: stream.getAudioTracks().length > 0,
      });

      // Камеру отпускаем сразу после записи.
      //
      // Иначе индикатор съёмки продолжает гореть, пока человек смотрит
      // результат, — выглядит так, будто мы снимаем и дальше. Плюс живой поток
      // перебивал предпросмотр: у элемента `srcObject` сильнее `src`.
      //
      // Ссылку обнуляем, а не только глушим дорожки: `start` берёт поток
      // из неё, и остановленный поток дал бы запись без картинки.
      stream.getTracks().forEach((track) => track.stop());
      streamRef.current = null;

      setState('recorded');
    };

    recorderRef.current = recorder;
    startedAtRef.current = Date.now();
    // Кусками по секунде: при обрыве останется хотя бы часть.
    recorder.start(1000);
    setState('recording');
    setElapsedSec(0);

    timerRef.current = window.setInterval(() => {
      setElapsedSec((Date.now() - startedAtRef.current) / 1000);
    }, 200);

    // Экран не должен гаснуть посреди чтения текста.
    try {
      const wakeLock = navigator as Navigator & {
        wakeLock?: { request: (type: 'screen') => Promise<{ release: () => Promise<void> }> };
      };
      wakeLockRef.current = (await wakeLock.wakeLock?.request('screen')) ?? null;
    } catch {
      // Wake lock не критичен: без него запись всё равно идёт.
    }
  }, [requestPermission]);

  const stop = useCallback(() => {
    stopTimer();
    recorderRef.current?.stop();
    void wakeLockRef.current?.release().catch(() => undefined);
    wakeLockRef.current = null;
  }, []);

  const reset = useCallback(() => {
    if (captured) URL.revokeObjectURL(captured.url);
    setCaptured(null);
    setElapsedSec(0);
    setState(streamRef.current ? 'ready' : 'idle');
  }, [captured]);

  const release = useCallback(() => {
    stopTimer();
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    void wakeLockRef.current?.release().catch(() => undefined);
  }, []);

  useEffect(() => release, [release]);

  /** Записанное не должно теряться от случайного ухода со страницы. */
  useEffect(() => {
    if (state !== 'recording' && state !== 'recorded') return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [state]);

  return {
    state,
    error,
    elapsedSec,
    captured,
    stream: streamRef,
    requestPermission,
    start,
    stop,
    reset,
    release,
  };
}
